import { AuthProvider, useAuth } from "@livcg/nodecg-react";
import { StrictMode, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "../shared/styles.css";
import { setReportingSlug, startClientReporting, useUnseenProblems } from "../shared/oplog.js";
import { useActiveProject } from "../shared/useActiveProject.js";
import { ToastProvider } from "../ui/index.js";
import { ClassicView } from "./ClassicView.js";
import { ControlRoom } from "./control-room/ControlRoom.js";
import { DeckView } from "./deck/DeckView.js";
import { EfirView } from "./efir/EfirView.js";
import { Login } from "./Login.js";
import { MatchSyncBanner } from "./SyncBanner.js";
import { OpLogDrawer } from "./OpLogDrawer.js";
import { type MatchDayView, ProjectBar } from "./ProjectBar.js";
import { ProjectPicker } from "./ProjectPicker.js";
import { ActivityMarks } from "./presence/ActivityMarks.js";
import { CursorLayer } from "./presence/CursorLayer.js";
import { PresenceAvatars } from "./presence/PresenceAvatars.js";
import { usePointerBroadcast } from "./presence/usePointerBroadcast.js";
import { usePresence } from "./presence/usePresence.js";

const VIEW_KEY = "livcg:matchday:view";
const CURSORS_KEY = "livcg-cursors-hidden";

function readView(): MatchDayView {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "control" || v === "deck" || v === "efir" ? v : "classic";
  } catch {
    return "classic";
  }
}

function MatchDay() {
  const auth = useAuth();
  const { slug, state, isLoading, select, clear } = useActiveProject();
  const [view, setView] = useState<MatchDayView>(readView);
  const [logOpen, setLogOpen] = useState(false);
  const unseenProblems = useUnseenProblems(slug, logOpen);
  const presence = usePresence(auth.user ? slug : null, view);
  usePointerBroadcast(!!(auth.user && slug), presence.sendCursor);
  const [cursorsHidden, setCursorsHidden] = useState(() => {
    try {
      return localStorage.getItem(CURSORS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleCursors = () =>
    setCursorsHidden((h) => {
      try {
        localStorage.setItem(CURSORS_KEY, h ? "0" : "1");
      } catch {
        /* private mode — ignore */
      }
      return !h;
    });

  useEffect(() => {
    if (auth.user) startClientReporting();
  }, [auth.user]);
  useEffect(() => setReportingSlug(slug), [slug]);

  const changeView = (next: MatchDayView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* private mode — ignore */
    }
  };

  // If the session ends (logout or an expired cookie) the next person to sign in
  // must not land on the match the previous operator picked. On logout the URL is
  // cleared too; but when the page is opened without a login the ?slug= link is kept.
  const signedOut = !auth.loading && !auth.user;
  const wasSignedIn = useRef(false);
  useEffect(() => {
    if (signedOut) clear({ keepUrl: !wasSignedIn.current });
    else if (auth.user) wasSignedIn.current = true;
  }, [signedOut, auth.user, clear]);

  if (auth.loading || isLoading) {
    return <div className="grid place-items-center h-full text-ink-muted">Yuklanmoqda…</div>;
  }
  if (!auth.user) {
    return <Login />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ProjectBar
        activeSlug={slug}
        activeName={state?.name ?? null}
        view={view}
        onViewChange={changeView}
        onLeave={() => clear()}
        onOpenLog={() => setLogOpen(true)}
        unseenProblems={unseenProblems}
        presence={
          <PresenceAvatars
            members={presence.members}
            selfId={presence.selfId}
            cursorsHidden={cursorsHidden}
            onToggleCursors={toggleCursors}
          />
        }
      />
      {slug && state && <MatchSyncBanner slug={slug} state={state} />}

      {slug && state ? (
        view === "efir" ? (
          <EfirView slug={slug} state={state} />
        ) : view === "control" ? (
          <ControlRoom slug={slug} state={state} />
        ) : view === "deck" ? (
          <DeckView slug={slug} state={state} />
        ) : (
          <ClassicView slug={slug} state={state} />
        )
      ) : (
        <ProjectPicker onSelect={select} />
      )}

      <OpLogDrawer open={logOpen} onClose={() => setLogOpen(false)} slug={slug} />
      {slug && !cursorsHidden && (
        <CursorLayer
          cursors={presence.cursors}
          members={presence.members}
          selfId={presence.selfId}
        />
      )}
      {slug && <ActivityMarks activity={presence.activity} me={auth.user.username} />}
    </div>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <MatchDay />
      </ToastProvider>
    </AuthProvider>
  </StrictMode>,
);
